import { FC } from 'react';
import {
  ExternalLink,
  Tag,
  Ticket,
  Clock,
  Send,
  Calendar,
  Layers,
  Copy,
  Check,
} from 'lucide-react';
import { PublicShowcaseOfferItem } from '../../api/showcase';
import { Drawer } from '../ui/Drawer';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

export interface ShowcaseOfferDrawerProps {
  offer: PublicShowcaseOfferItem | null;
  isOpen: boolean;
  onClose: () => void;
  primaryColor: string;
  isCopied: boolean;
  onCopyCoupon: () => void;
}

export const ShowcaseOfferDrawer: FC<ShowcaseOfferDrawerProps> = ({
  offer,
  isOpen,
  onClose,
  primaryColor,
  isCopied,
  onCopyCoupon,
}) => {
  if (!offer) return null;

  // Formatação segura de data de envio
  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  const sentAtFormatted = formatDateTime(offer.sent_at);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      width="lg"
      title={
        <div className="flex items-center gap-2 pr-4">
          <Badge
            variant="neutral"
            className="font-mono text-micro font-bold uppercase"
          >
            {offer.marketplace}
          </Badge>
          {offer.discount_percent && offer.discount_percent > 0 && (
            <span
              style={{ backgroundColor: primaryColor }}
              className="rounded-lg px-2 py-0.5 text-xs font-extrabold text-white shadow-sm"
            >
              -{offer.discount_percent}% OFF
            </span>
          )}
        </div>
      }
      description="Detalhes completos da promoção e histórico de envio"
      footer={
        <div className="flex w-full items-center gap-2">
          <Button variant="secondary" onClick={onClose} className="w-1/3">
            Fechar
          </Button>
          <a
            href={offer.tracked_url}
            target="_blank"
            rel="noreferrer"
            style={{ backgroundColor: primaryColor }}
            className="shadow-xs inline-flex w-2/3 cursor-pointer items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-bold text-white transition-all duration-150 hover:opacity-95 active:scale-[0.99] sm:text-sm"
          >
            <span>Ir para a Loja Oficial</span>
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      }
    >
      <div className="space-y-6">
        {/* 1. Imagem de Alta Qualidade */}
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-surface-border bg-surface-subtle">
          {offer.image_url ? (
            <img
              src={offer.image_url}
              alt={offer.title}
              className="h-full w-full object-contain p-2"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-content-muted">
              {offer.is_coupon_only ? (
                <Ticket className="h-16 w-16 opacity-30" />
              ) : (
                <Tag className="h-16 w-16 opacity-30" />
              )}
            </div>
          )}
        </div>

        {/* 2. Título da Oferta e Categorias */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {offer.categories.map((cat) => (
              <Badge key={cat} variant="neutral" className="text-micro">
                {cat}
              </Badge>
            ))}
          </div>
          <h2 className="text-base font-bold leading-snug text-content-main sm:text-lg">
            {offer.title}
          </h2>
        </div>

        {/* 3. Bloco de Preço e Economia */}
        <div className="bg-surface-subtle/50 rounded-2xl border border-surface-border p-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <div>
              {offer.original_price && offer.promotional_price && (
                <span className="text-xs text-content-muted line-through">
                  De R$ {offer.original_price.toFixed(2).replace('.', ',')}
                </span>
              )}
              {offer.promotional_price ? (
                <div className="text-2xl font-black text-content-main">
                  <span className="text-sm font-bold text-content-muted">
                    R${' '}
                  </span>
                  {offer.promotional_price.toFixed(2).replace('.', ',')}
                </div>
              ) : offer.is_coupon_only ? (
                <div className="text-base font-bold text-brand-600 dark:text-brand-400">
                  Cupom Promocional
                </div>
              ) : null}
            </div>

            {offer.discount_percent && offer.discount_percent > 0 && (
              <div className="flex items-center gap-1.5">
                <Badge variant="online" className="text-xs">
                  Economia de {offer.discount_percent}%
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* 4. Bloco de Cupom de Desconto */}
        {offer.coupon_code && (
          <div className="rounded-2xl border border-dashed border-brand-500/40 bg-brand-500/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <span className="text-2xs font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
                  Cupom de Desconto
                </span>
                <div className="font-mono text-base font-black tracking-wide text-content-main">
                  {offer.coupon_code}
                </div>
              </div>

              <button
                type="button"
                onClick={onCopyCoupon}
                className="shadow-xs inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-surface-panel px-3 py-2 text-xs font-bold text-brand-600 transition-colors hover:bg-surface-subtle dark:text-brand-400"
              >
                {isCopied ? (
                  <>
                    <Check className="h-4 w-4 text-brand-500" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Copiar Cupom</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* 5. Alerta de Idade / Validade */}
        {offer.is_age_warning && (
          <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
            <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="space-y-0.5">
              <strong className="block font-semibold">
                Atenção ao estoque e preço
              </strong>
              <p className="text-2xs leading-relaxed opacity-90">
                Esta promoção foi publicada há alguns dias. É possível que o
                preço tenha sido reajustado ou os cupons tenham esgotado na
                loja.
              </p>
            </div>
          </div>
        )}

        {/* 6. Linha do Tempo e Telemetria de Envio */}
        <div className="space-y-3 rounded-2xl border border-surface-border bg-surface-panel p-4">
          <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-content-muted">
            <Send className="h-3.5 w-3.5" />
            <span>Telemetria de Distribuição</span>
          </h3>

          <div className="space-y-2 text-xs">
            <div className="border-surface-border/60 flex items-center justify-between border-b pb-2">
              <span className="flex items-center gap-1.5 text-content-muted">
                <Calendar className="h-3.5 w-3.5" />
                <span>Data de Envio</span>
              </span>
              <span className="font-medium text-content-main">
                {sentAtFormatted ? (
                  sentAtFormatted
                ) : (
                  <span className="italic text-content-muted">
                    Não registrado
                  </span>
                )}
              </span>
            </div>

            <div className="border-surface-border/60 flex items-center justify-between border-b pb-2">
              <span className="flex items-center gap-1.5 text-content-muted">
                <Layers className="h-3.5 w-3.5" />
                <span>Canal de Origem</span>
              </span>
              <Badge variant="online" className="text-micro font-medium">
                Grupos & Canais WhatsApp
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-content-muted">
                <Tag className="h-3.5 w-3.5" />
                <span>Código Curto / Ref</span>
              </span>
              <span className="font-mono text-xs font-semibold text-content-main">
                {offer.short_code}
              </span>
            </div>
          </div>
        </div>

        {/* 7. Texto / Copy Promocional Enviada */}
        {offer.selected_copy && (
          <div className="space-y-2 rounded-2xl border border-surface-border bg-surface-panel p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-content-muted">
              Mensagem Divulgada
            </h3>
            <div className="whitespace-pre-wrap rounded-xl bg-surface-subtle p-3 text-xs leading-relaxed text-content-main">
              {offer.selected_copy}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};
